import React, {useState, useEffect, useCallback} from "react"
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  alpha,
  useTheme,
  Stack,
  IconButton,
  Collapse,
  Badge,
} from "@mui/material"
import AudioPlayer from "components/AudioPlayer.jsx"
import {useAuth0} from "@auth0/auth0-react"
import {baseURL} from "constants/api"
import {useStateContext} from "contexts/userContext"
import {useTranslation} from "react-i18next"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import PersonIcon from "@mui/icons-material/Person"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PaymentIcon from "@mui/icons-material/Payment"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import RestaurantIcon from "@mui/icons-material/Restaurant"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"

export function Orders() {
  const {isAuthenticated} = useAuth0()
  const [orders, setOrders] = useState([])
  const [ordersReverse, setOrdersReverse] = useState([])
  const [loading, setLoading] = useState(true)
  const [volumeValueDataReceived, setVolumeValueDataReceived] = useState(0)
  const [expandedOrders, setExpandedOrders] = useState({})
  const {restaurant_id} = useStateContext()
  const {t} = useTranslation()
  const theme = useTheme()

  //==================================================

  // Функция для получения данных из сервера
  const fetchData = useCallback(async () => {
    if (!restaurant_id || restaurant_id === null) return
    
    try {
      const response = await fetch(`${ baseURL }/orders/${ restaurant_id }`)

      if (response.ok) {
        const newData = await response.json()
        if (newData.length != orders.length) {
          console.log("Данные изменились:", newData)
          setOrders(newData)
          setOrdersReverse(newData.reverse())

          setVolumeValueDataReceived(1)
          setTimeout(() => {
            setVolumeValueDataReceived(0)
          }, 3000)
        }
      }
      setLoading(false)
    } catch (error) {
      console.error("Ошибка при запросе данных:", error)
      setLoading(false)
    }
  }, [restaurant_id, orders])

  useEffect(() => {
    if (restaurant_id === null) return
    
    fetchData() // Выполняем запрос сразу
    const interval = setInterval(fetchData, 3000)

    return () => clearInterval(interval) // Очищаем интервал при размонтировании компонента
  }, [restaurant_id, fetchData]) // Зависимость от restaurant_id и fetchData для обновления при смене ресторана

  const parseCartItems = (cartItems) => {
    if (!cartItems) return []
    try {
      const parsed = JSON.parse(cartItems)
      return Array.isArray(parsed) ? parsed : []
    } catch (error) {
      console.error("Error parsing cartItems:", error)
      return []
    }
  }

  const getOrderStatus = (orderDate) => {
    const currentTime = new Date()
    const orderTime = new Date(orderDate)
    const timeDifference = (currentTime - orderTime) / (1000 * 60)

    if (timeDifference <= 5) {
      return {
        label: t("New"),
        color: "#10b981",
        bgcolor: alpha("#10b981", 0.1),
      }
    } else if (timeDifference <= 10) {
      return {
        label: t("Pending"),
        color: "#f59e0b",
        bgcolor: alpha("#f59e0b", 0.1),
      }
    } else {
      return {
        label: t("Urgent"),
        color: "#ef4444",
        bgcolor: alpha("#ef4444", 0.1),
      }
    }
  }

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }))
  }

  const dateToTime = (orderDateServer) => {
    const orderDate = new Date(orderDateServer)

    const hours = String(orderDate.getHours()).padStart(2, "0")
    const minutes = String(orderDate.getMinutes()).padStart(2, "0")
    const seconds = String(orderDate.getSeconds()).padStart(2, "0")

    const day = String(orderDate.getDate()).padStart(2, "0")
    const month = String(orderDate.getMonth() + 1).padStart(2, "0") // Месяцы в JavaScript начинаются с 0, поэтому добавляем 1
    const year = orderDate.getFullYear()

    const formattedDate = `${ hours }:${ minutes }:${ seconds }--${ day }/${ month }/${ year }`
    return formattedDate
  }
  return (
    <>
      {isAuthenticated && (
        <Box
          sx={{
            p: {xs: 2, md: 4},
            bgcolor: "background.default",
            minHeight: "100vh",
          }}
        >
          {/* Header */}
          <Box sx={{mb: 4}}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ReceiptLongIcon
                    sx={{fontSize: 32, color: "primary.main"}}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      fontSize: {xs: "1.75rem", md: "2.125rem"},
                    }}
                  >
                    {t("Orders")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{color: "text.secondary", mt: 0.5}}
                  >
                    {t("Monitor and manage incoming orders")}
                  </Typography>
                </Box>
              </Box>
              <AudioPlayer volumeValueDataReceived={volumeValueDataReceived} />
            </Box>

            {/* Status Legend */}
            <Stack
              direction={{xs: "column", sm: "row"}}
              spacing={2}
              sx={{
                mt: 3,
                p: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                borderRadius: "12px",
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              {[
                {
                  label: t("less than 5 minutes ago"),
                  color: "#10b981",
                  icon: "🟢",
                },
                {
                  label: t("5-10 minutes ago"),
                  color: "#f59e0b",
                  icon: "🟡",
                },
                {
                  label: t("more than 10 minutes ago"),
                  color: "#ef4444",
                  icon: "🔴",
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      boxShadow: `0 0 8px ${alpha(item.color, 0.4)}`,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{color: "text.secondary", fontWeight: 500}}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Orders List */}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <CircularProgress size={48} />
            </Box>
          ) : orders.length > 0 ? (
            <Stack spacing={2}>
              {ordersReverse.map((order) => {
                const status = getOrderStatus(order.order_date)
                const cartItems = parseCartItems(order.cartItems) || []
                const isExpanded = expandedOrders[order.order_id]

                return (
                  <Card
                    key={order.order_id}
                    sx={{
                      transition: "all 0.3s ease",
                      border: `2px solid ${status.color}`,
                      boxShadow: `0 4px 12px ${alpha(status.color, 0.15)}`,
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 24px ${alpha(status.color, 0.25)}`,
                      },
                    }}
                  >
                    <CardContent sx={{p: 3}}>
                      {/* Order Header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                          <Chip
                            label={`#${order.order_id}`}
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: "primary.main",
                            }}
                          />
                          <Chip
                            label={status.label}
                            sx={{
                              fontWeight: 600,
                              bgcolor: status.bgcolor,
                              color: status.color,
                            }}
                            size="small"
                          />
                        </Box>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                          <AccessTimeIcon
                            sx={{fontSize: 18, color: "text.secondary"}}
                          />
                          <Typography
                            variant="body2"
                            sx={{color: "text.secondary"}}
                          >
                            {dateToTime(order.order_date)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Order Summary Grid */}
                      <Grid container spacing={2} sx={{mb: 2}}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.5),
                              borderRadius: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <PaymentIcon
                                sx={{fontSize: 16, color: "text.secondary"}}
                              />
                              <Typography
                                variant="caption"
                                sx={{color: "text.secondary", fontWeight: 600}}
                              >
                                {t("Total Price")}
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{fontWeight: 700, color: "text.primary"}}
                            >
                              {order.totalPrice || "-"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.5),
                              borderRadius: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <LocalShippingIcon
                                sx={{fontSize: 16, color: "text.secondary"}}
                              />
                              <Typography
                                variant="caption"
                                sx={{color: "text.secondary", fontWeight: 600}}
                              >
                                {t("Delivery Option")}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{fontWeight: 600, color: "text.primary"}}
                            >
                              {order.optionDelivery || "-"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.5),
                              borderRadius: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <PersonIcon
                                sx={{fontSize: 16, color: "text.secondary"}}
                              />
                              <Typography
                                variant="caption"
                                sx={{color: "text.secondary", fontWeight: 600}}
                              >
                                {t("Username")}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{fontWeight: 600, color: "text.primary"}}
                            >
                              {order.customer_name || order.user_name || "-"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.5),
                              borderRadius: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <PaymentIcon
                                sx={{fontSize: 16, color: "text.secondary"}}
                              />
                              <Typography
                                variant="caption"
                                sx={{color: "text.secondary", fontWeight: 600}}
                              >
                                {t("Payment Method")}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{fontWeight: 600, color: "text.primary"}}
                            >
                              {order.paymentMethod || "-"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Address & Comment */}
                      {(order.address || order.comment) && (
                        <Stack spacing={1.5} sx={{mb: 2}}>
                          {order.address && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                p: 1.5,
                                bgcolor: alpha("#3b82f6", 0.05),
                                borderRadius: "8px",
                              }}
                            >
                              <LocationOnIcon
                                sx={{fontSize: 20, color: "#3b82f6", mt: 0.2}}
                              />
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  {t("Address")}
                                </Typography>
                                <Typography variant="body2">
                                  {order.address}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {order.comment && (
                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: alpha("#8b5cf6", 0.05),
                                borderRadius: "8px",
                                borderLeft: `3px solid #8b5cf6`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 600,
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                {t("Comment")}
                              </Typography>
                              <Typography variant="body2">
                                {order.comment}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      )}

                      <Divider sx={{my: 2}} />

                      {/* Order Items Toggle */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          p: 1,
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                        onClick={() => toggleOrderExpanded(order.order_id)}
                      >
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                          <RestaurantIcon
                            sx={{fontSize: 20, color: "primary.main"}}
                          />
                          <Typography
                            variant="body1"
                            sx={{fontWeight: 600, color: "text.primary"}}
                          >
                            {t("Cart Items")}
                          </Typography>
                          <Badge
                            badgeContent={cartItems.length}
                            color="primary"
                            sx={{ml: 1}}
                          />
                        </Box>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      {/* Order Items Details */}
                      <Collapse in={isExpanded}>
                        <Box sx={{mt: 2}}>
                          {cartItems.length > 0 ? (
                            <Stack spacing={2}>
                              {cartItems.map((item, index) => (
                                <Card
                                  key={index}
                                  variant="outlined"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.background.default,
                                      0.3
                                    ),
                                  }}
                                >
                                  <CardContent sx={{p: 2, "&:last-child": {pb: 2}}}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{fontWeight: 700, color: "text.primary"}}
                                      >
                                        {item.title}
                                      </Typography>
                                      <Chip
                                        label={`× ${item.quantity}`}
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          bgcolor: alpha(
                                            theme.palette.primary.main,
                                            0.1
                                          ),
                                          color: "primary.main",
                                        }}
                                      />
                                    </Box>

                                    {/* Choices */}
                                    {item?.selectedChoices?.length > 0 && (
                                      <Box sx={{mb: 1.5}}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 700,
                                            color: "text.secondary",
                                            textTransform: "uppercase",
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {t("Choices")}
                                        </Typography>
                                        <Stack spacing={0.5} sx={{mt: 0.5}}>
                                          {item.selectedChoices.map((choice) => (
                                            <Typography
                                              key={choice.id}
                                              variant="body2"
                                              sx={{pl: 1.5}}
                                            >
                                              • {choice.name}:{" "}
                                              <strong>
                                                {choice.options[0].name}
                                              </strong>
                                            </Typography>
                                          ))}
                                        </Stack>
                                      </Box>
                                    )}

                                    {/* Addons */}
                                    {item?.selectedAddons?.length > 0 && (
                                      <Box sx={{mb: 1.5}}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 700,
                                            color: "text.secondary",
                                            textTransform: "uppercase",
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {t("Addons")}
                                        </Typography>
                                        <Stack spacing={0.5} sx={{mt: 0.5}}>
                                          {item.selectedAddons.map((addon) =>
                                            addon.options.map((option) => (
                                              <Typography
                                                key={option.id}
                                                variant="body2"
                                                sx={{pl: 1.5}}
                                              >
                                                • {addon.name}:{" "}
                                                <strong>{option.name}</strong>
                                              </Typography>
                                            ))
                                          )}
                                        </Stack>
                                      </Box>
                                    )}

                                    {/* Toppings */}
                                    {item?.selectedToppings?.length > 0 && (
                                      <Box sx={{mb: 1.5}}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 700,
                                            color: "text.secondary",
                                            textTransform: "uppercase",
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {t("Toppings")}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                            mt: 0.5,
                                          }}
                                        >
                                          {item.selectedToppings.map(
                                            (topping, toppingIndex) => (
                                              <Chip
                                                key={toppingIndex}
                                                label={topping}
                                                size="small"
                                                variant="outlined"
                                              />
                                            )
                                          )}
                                        </Box>
                                      </Box>
                                    )}

                                    {/* Extras */}
                                    {item?.selectedExtrasNames &&
                                      Object.keys(item.selectedExtrasNames)
                                        .length > 0 && (
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 700,
                                              color: "text.secondary",
                                              textTransform: "uppercase",
                                              letterSpacing: 0.5,
                                            }}
                                          >
                                            {t("Extras")}
                                          </Typography>
                                          <Stack spacing={0.5} sx={{mt: 0.5}}>
                                            {Object.keys(
                                              item.selectedExtrasNames
                                            ).map((key, idx) => (
                                              <Typography
                                                key={idx}
                                                variant="body2"
                                                sx={{pl: 1.5}}
                                              >
                                                • {key} -{" "}
                                                <strong>
                                                  {item.selectedExtrasNames[key]}
                                                </strong>
                                              </Typography>
                                            ))}
                                          </Stack>
                                        </Box>
                                      )}
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{color: "text.secondary", textAlign: "center", py: 2}}
                            >
                              {t("No items")}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                )
              })}
            </Stack>
          ) : (
            <Card
              sx={{
                p: 6,
                textAlign: "center",
                border: `2px dashed ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              <ReceiptLongIcon
                sx={{fontSize: 64, color: "text.secondary", mb: 2}}
              />
              <Typography variant="h6" sx={{fontWeight: 600, mb: 1}}>
                {t("No Orders To Display")}
              </Typography>
              <Typography variant="body2" sx={{color: "text.secondary"}}>
                {t("New orders will appear here automatically")}
              </Typography>
            </Card>
          )}
        </Box>
      )}
    </>
  )
}
